import { useReducer, useState, useCallback } from 'react';
import { ActivityLogFormData, ActivityLogValidation, ActivityLog } from 'containers/pages/activityLogManagement/types';
import { ConsultantOption } from 'api/cases/searchConsultants';
import { CustomerOption } from 'api/cases/searchCustomers';
import { ContactOption } from 'api/cases/searchContacts';
import { StatusOption } from 'api/cases/searchStatuses';
import { GPDOption } from 'api/cases/searchGPD';
import searchConsultants from 'api/cases/searchConsultants';
import searchCustomers from 'api/cases/searchCustomers';
import searchStatuses from 'api/cases/searchStatuses';
import searchContacts from 'api/cases/searchContacts';
import { searchGroups, searchProjects, searchDeliverables } from 'api/cases/searchGPD';

const initForm: ActivityLogFormData = {
  Title: '',
  Description: '',
  StatusComment: '',
  StatusId: null,
  StatusTitle: '',
  CustomerId: null,
  CustomerName: '',
  ConsultantId: null,
  ConsultantName: '',
  PrimaryContactId: null,
  PrimaryContactName: '',
  GroupId: null,
  GroupTitle: '',
  ProjectId: null,
  ProjectTitle: '',
  DeliverableId: null,
  DeliverableTitle: '',
  Private: false,
  IpRestricted: false,
};

const initValid: ActivityLogValidation = {
  Title: { isErr: false, errMsg: '', canNotSave: true },
  StatusId: { isErr: false, errMsg: '', canNotSave: true },
  CustomerId: { isErr: false, errMsg: '', canNotSave: true },
  ConsultantId: { isErr: false, errMsg: '', canNotSave: true },
};

const loadedValid: ActivityLogValidation = {
  Title: { isErr: false, errMsg: '', canNotSave: false },
  StatusId: { isErr: false, errMsg: '', canNotSave: false },
  CustomerId: { isErr: false, errMsg: '', canNotSave: false },
  ConsultantId: { isErr: false, errMsg: '', canNotSave: false },
};

interface FormAction {
  type: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  payload: any;
}

const formReducer = (state: ActivityLogFormData, action: FormAction): ActivityLogFormData => {
  switch (action.type) {
    case 'RESET':
      return action.payload;
    default:
      if (action.type in state) {
        return { ...state, [action.type]: action.payload };
      }
      return state;
  }
};

interface UseActivityLogFormProps {
  mode: 'add' | 'edit';
  activityLog?: ActivityLog | null;
  onSave?: (formData: ActivityLogFormData, isEdit: boolean) => Promise<void>;
}

function useActivityLogForm({ mode, activityLog, onSave }: UseActivityLogFormProps) {
  const getInitialForm = (): ActivityLogFormData => {
    if (mode === 'edit' && activityLog) {
      return {
        Id: activityLog.Id,
        Title: activityLog.Title || '',
        Description: activityLog.Description || '',
        StatusComment: activityLog.StatusComment || '',
        StatusId: activityLog.StatusId || null,
        StatusTitle: activityLog.StatusTitle || '',
        CustomerId: activityLog.CustomerId || null,
        CustomerName: activityLog.CustomerName || '',
        ConsultantId: activityLog.ConsultantId || null,
        ConsultantName: activityLog.ConsultantName || '',
        PrimaryContactId: activityLog.PrimaryContactId || null,
        PrimaryContactName: activityLog.PrimaryContactName || '',
        GroupId: activityLog.GroupId || null,
        GroupTitle: activityLog.GroupTitle || '',
        ProjectId: activityLog.ProjectId || null,
        ProjectTitle: activityLog.ProjectTitle || '',
        DeliverableId: activityLog.DeliverableId || null,
        DeliverableTitle: activityLog.DeliverableTitle || '',
        Private: activityLog.Private || false,
        IpRestricted: activityLog.IpRestricted || false,
      };
    }
    return { ...initForm, StatusId: 1 };
  };

  const [formState, dispatch] = useReducer(formReducer, getInitialForm());
  const [valid, setValid] = useState<ActivityLogValidation>(
    mode === 'edit' ? { ...loadedValid } : { ...initValid },
  );

  // Search options state
  const [statusOptions, setStatusOptions] = useState<StatusOption[]>([]);
  const [customerOptions, setCustomerOptions] = useState<CustomerOption[]>([]);
  const [consultantOptions, setConsultantOptions] = useState<ConsultantOption[]>([]);
  const [contactOptions, setContactOptions] = useState<ContactOption[]>([]);
  const [groupOptions, setGroupOptions] = useState<GPDOption[]>([]);
  const [projectOptions, setProjectOptions] = useState<GPDOption[]>([]);
  const [deliverableOptions, setDeliverableOptions] = useState<GPDOption[]>([]);

  // Text field handlers
  const onForm = useCallback((e: { target: { name: string; value: string } }) => {
    const name = e.target.name.trim();
    const value = e.target.value;
    dispatch({ type: name, payload: value });

    if (name === 'Title') {
      setValid((prev) => ({
        ...prev,
        Title: {
          isErr: !value.trim(),
          errMsg: !value.trim() ? 'Title is required' : '',
          canNotSave: !value.trim(),
        },
      }));
    }
  }, []);

  // Status autocomplete
  const onStatusChange = useCallback((_e: unknown, val: StatusOption | null) => {
    dispatch({ type: 'StatusId', payload: val?.Id || null });
    dispatch({ type: 'StatusTitle', payload: val?.Title || '' });
    setValid((prev) => ({
      ...prev,
      StatusId: {
        isErr: !val,
        errMsg: !val ? 'Status is required' : '',
        canNotSave: !val,
      },
    }));
  }, []);

  const onStatusSearch = useCallback((_e: unknown, val: string) => {
    searchStatuses(val).then(setStatusOptions).catch(() => {});
  }, []);

  // Customer autocomplete
  const onCustomerChange = useCallback((_e: unknown, val: CustomerOption | null) => {
    dispatch({ type: 'CustomerId', payload: val?.Id || null });
    dispatch({ type: 'CustomerName', payload: val?.Name || '' });
    // Clear dependent fields
    dispatch({ type: 'PrimaryContactId', payload: null });
    dispatch({ type: 'PrimaryContactName', payload: '' });
    dispatch({ type: 'GroupId', payload: null });
    dispatch({ type: 'GroupTitle', payload: '' });
    dispatch({ type: 'ProjectId', payload: null });
    dispatch({ type: 'ProjectTitle', payload: '' });
    dispatch({ type: 'DeliverableId', payload: null });
    dispatch({ type: 'DeliverableTitle', payload: '' });
    setContactOptions([]);
    setGroupOptions([]);
    setProjectOptions([]);
    setDeliverableOptions([]);
    setValid((prev) => ({
      ...prev,
      CustomerId: {
        isErr: !val,
        errMsg: !val ? 'Customer is required' : '',
        canNotSave: !val,
      },
    }));
  }, []);

  const onCustomerSearch = useCallback((_e: unknown, val: string) => {
    searchCustomers(val).then(setCustomerOptions).catch(() => {});
  }, []);

  // Consultant autocomplete
  const onConsultantChange = useCallback((_e: unknown, val: ConsultantOption | null) => {
    dispatch({ type: 'ConsultantId', payload: val?.Id || null });
    dispatch({ type: 'ConsultantName', payload: val?.FullName || '' });
    setValid((prev) => ({
      ...prev,
      ConsultantId: {
        isErr: !val,
        errMsg: !val ? 'Consultant is required' : '',
        canNotSave: !val,
      },
    }));
  }, []);

  const onConsultantSearch = useCallback((_e: unknown, val: string) => {
    searchConsultants(val).then(setConsultantOptions).catch(() => {});
  }, []);

  // Contact autocomplete (depends on Customer)
  const onContactChange = useCallback((_e: unknown, val: ContactOption | null) => {
    dispatch({ type: 'PrimaryContactId', payload: val?.Id || null });
    dispatch({ type: 'PrimaryContactName', payload: val?.FullName || '' });
  }, []);

  const onContactSearch = useCallback(
    (_e: unknown, val: string) => {
      if (formState.CustomerId) {
        searchContacts(formState.CustomerId, val).then(setContactOptions).catch(() => {});
      }
    },
    [formState.CustomerId],
  );

  // Group autocomplete (depends on Customer)
  const onGroupChange = useCallback((_e: unknown, val: GPDOption | null) => {
    dispatch({ type: 'GroupId', payload: val?.Id || null });
    dispatch({ type: 'GroupTitle', payload: val?.Title || '' });
    // Clear downstream
    dispatch({ type: 'ProjectId', payload: null });
    dispatch({ type: 'ProjectTitle', payload: '' });
    dispatch({ type: 'DeliverableId', payload: null });
    dispatch({ type: 'DeliverableTitle', payload: '' });
    setProjectOptions([]);
    setDeliverableOptions([]);
  }, []);

  const onGroupSearch = useCallback(
    (_e: unknown, val: string) => {
      if (formState.CustomerId) {
        searchGroups(formState.CustomerId, val).then(setGroupOptions).catch(() => {});
      }
    },
    [formState.CustomerId],
  );

  // Project autocomplete (depends on Group)
  const onProjectChange = useCallback((_e: unknown, val: GPDOption | null) => {
    dispatch({ type: 'ProjectId', payload: val?.Id || null });
    dispatch({ type: 'ProjectTitle', payload: val?.Title || '' });
    // Clear downstream
    dispatch({ type: 'DeliverableId', payload: null });
    dispatch({ type: 'DeliverableTitle', payload: '' });
    setDeliverableOptions([]);
  }, []);

  const onProjectSearch = useCallback(
    (_e: unknown, val: string) => {
      if (formState.GroupId) {
        searchProjects(formState.GroupId, val).then(setProjectOptions).catch(() => {});
      }
    },
    [formState.GroupId],
  );

  // Deliverable autocomplete (depends on Project)
  const onDeliverableChange = useCallback((_e: unknown, val: GPDOption | null) => {
    dispatch({ type: 'DeliverableId', payload: val?.Id || null });
    dispatch({ type: 'DeliverableTitle', payload: val?.Title || '' });
  }, []);

  const onDeliverableSearch = useCallback(
    (_e: unknown, val: string) => {
      if (formState.ProjectId) {
        searchDeliverables(formState.ProjectId, val).then(setDeliverableOptions).catch(() => {});
      }
    },
    [formState.ProjectId],
  );

  const canNotSave = Object.values(valid).some((v) => v.canNotSave);

  const onSaveForm = useCallback(async () => {
    const current = formState;
    const newValid = { ...valid };
    let hasErrors = false;

    if (!current.Title?.trim()) {
      newValid.Title = { isErr: true, errMsg: 'Title is required', canNotSave: true };
      hasErrors = true;
    }
    if (!current.StatusId) {
      newValid.StatusId = { isErr: true, errMsg: 'Status is required', canNotSave: true };
      hasErrors = true;
    }
    if (!current.CustomerId) {
      newValid.CustomerId = { isErr: true, errMsg: 'Customer is required', canNotSave: true };
      hasErrors = true;
    }
    if (!current.ConsultantId) {
      newValid.ConsultantId = { isErr: true, errMsg: 'Consultant is required', canNotSave: true };
      hasErrors = true;
    }

    if (hasErrors) {
      setValid(newValid);
      return;
    }

    if (onSave) {
      await onSave(current, mode === 'edit');
    }
  }, [formState, valid, mode, onSave]);

  const onReset = useCallback(() => {
    if (mode === 'add') {
      dispatch({ type: 'RESET', payload: { ...initForm, StatusId: 1 } });
      setValid({ ...initValid });
    }
    if (mode === 'edit' && activityLog) {
      dispatch({ type: 'RESET', payload: getInitialForm() });
      setValid({ ...loadedValid });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, activityLog]);

  const state = {
    form: formState,
    valid,
    canNotSave,
    statusOptions,
    customerOptions,
    consultantOptions,
    contactOptions,
    groupOptions,
    projectOptions,
    deliverableOptions,
  };

  const handler = {
    onForm,
    onStatusChange,
    onStatusSearch,
    onCustomerChange,
    onCustomerSearch,
    onConsultantChange,
    onConsultantSearch,
    onContactChange,
    onContactSearch,
    onGroupChange,
    onGroupSearch,
    onProjectChange,
    onProjectSearch,
    onDeliverableChange,
    onDeliverableSearch,
    onSave: onSaveForm,
    onReset,
  };

  return { state, handler };
}

export default useActivityLogForm;
